import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ApiError, api } from '@/api/client';
import { useApi } from '@/api/useApi';
import { useSession } from '@/api/session';
import type { ClientRecord, ScheduleHealth } from '@/api/types';
import {
  Button,
  Card,
  CardHead,
  ErrorState,
  Field,
  Loading,
  Modal,
  Notice,
  Page,
  PageHead,
  Tag,
  Table,
  Tabs,
  TextInput,
} from '@/components/ui';
import { RecordNoteOnBehalf } from '@/components/RecordNoteOnBehalf';
import { describePlanItem } from '@/lib/describePlan';
import { formatDate, formatRange, fullAddress, humanise } from '@/lib/format';

type Section = 'plan' | 'history' | 'visits' | 'feedback' | 'access';

/**
 * The client record is the centre of operations (handoff p.8): the current
 * plan, its history, visits and notes, feedback, and who has access — all in
 * one place, rather than scattered across a plan library and a people list.
 */
export function ClientRecordPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [section, setSection] = useState<Section>('plan');
  const [inviting, setInviting] = useState(false);
  const [recordingFor, setRecordingFor] = useState<string | null>(null);
  const { data, error, loading, reload } = useApi<ClientRecord>(`/admin/clients/${id}`);

  if (loading) return <Loading />;
  if (error) return <Page><ErrorState message={error.message} onRetry={reload} /></Page>;
  if (!data) return null;

  const { client } = data;
  const consentRecorded = client.consent_status === 'granted';

  return (
    <Page>
      <PageHead
        breadcrumb={
          <nav className="breadcrumb" aria-label="Breadcrumb">
            <Link to="/clients">Clients</Link>
            <span aria-hidden="true">/</span>
            <span>{client.preferred_name ?? client.full_name}</span>
          </nav>
        }
        title={client.preferred_name ?? client.full_name}
        subtitle={`${humanise(client.service_status)} · ${fullAddress(client.address)}`}
        actions={
          <>
            <Button variant="secondary" onClick={() => setInviting(true)}>
              Invite family member
            </Button>
            <Button variant="secondary" onClick={() => navigate(`/clients/${id}/schedule`)}>
              Schedule &amp; caregivers
            </Button>
            <Button onClick={() => navigate(`/clients/${id}/plan`)}>
              {data.draft_plan ? 'Continue draft plan' : 'Edit care plan'}
            </Button>
          </>
        }
      />

      <div className="stack">
        {!consentRecorded ? (
          <Notice tone="attention" title="Consent is not recorded">
            Family access cannot be granted for this client until legal authority or consent is on
            file.
          </Notice>
        ) : null}

        <ScheduleGap health={data.schedule_health} clientId={id!} />

      {data.draft_plan ? (
          <Notice tone="attention" title="A plan update is awaiting publication">
            {data.draft_plan.change_reason ?? 'No reason recorded yet.'}
          </Notice>
        ) : null}

        <Tabs
          label="Client record sections"
          active={section}
          onChange={setSection}
          tabs={[
            { value: 'plan', label: 'Plan' },
            { value: 'history', label: 'History', count: data.version_history.length },
            { value: 'visits', label: 'Visits', count: data.visits.length },
            { value: 'feedback', label: 'Feedback', count: data.feedback_cases.length },
            { value: 'access', label: 'Access', count: data.authorized_people.length },
          ]}
        />

        {section === 'plan' ? <PlanTab data={data} /> : null}

        {section === 'history' ? (
          <Table
            columns={['Version', 'Status', 'Effective', 'Change', 'Published by']}
            empty="No plan versions yet."
          >
            {data.version_history.map((version) => (
              <tr key={version.id}>
                <td className="strong">v{version.version_number}</td>
                <td>
                  <div className="row">
                    <Tag
                      label={humanise(version.status)}
                      tone={
                        version.status === 'published'
                          ? 'positive'
                          : version.status === 'draft'
                            ? 'attention'
                            : 'neutral'
                      }
                    />
                    {version.is_material_change ? <Tag label="Material" tone="info" /> : null}
                  </div>
                </td>
                <td className="meta">{formatDate(version.effective_date)}</td>
                <td>{version.change_reason ?? <span className="meta">—</span>}</td>
                <td className="meta">{version.published_by ?? version.author ?? '—'}</td>
              </tr>
            ))}
          </Table>
        ) : null}

        {section === 'visits' ? (
          <Table
            columns={['Date', 'Time', 'Caregiver', 'State', 'Note', 'Family update', '']}
            empty="No visits recorded."
          >
            {data.visits.map((visit) => (
              <tr
                key={visit.id}
                data-clickable={visit.note_id ? 'true' : undefined}
                onClick={visit.note_id ? () => navigate(`/notes/${visit.note_id}`) : undefined}
              >
                <td className="strong">{formatDate(visit.scheduled_start)}</td>
                <td className="meta">{formatRange(visit.scheduled_start, visit.scheduled_end)}</td>
                <td>{visit.caregiver_name ?? <span className="meta">Unassigned</span>}</td>
                <td>
                  <Tag
                    label={humanise(visit.state)}
                    tone={visit.state === 'completed' ? 'positive' : 'info'}
                  />
                </td>
                <td>
                  {visit.note_status ? (
                    <Tag
                      label={humanise(visit.note_status)}
                      tone={visit.note_status === 'corrected' ? 'info' : 'positive'}
                    />
                  ) : (
                    <span className="meta">None</span>
                  )}
                </td>
                <td>
                  {visit.family_update_status === 'pending_approval' ? (
                    <Tag label="Awaiting approval" tone="attention" />
                  ) : visit.family_update_status === 'approved' ? (
                    <Tag label="Shared" tone="positive" />
                  ) : (
                    <span className="meta">—</span>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {/* The note queues only carry visits a caregiver marked
                      completed. A visit nobody ever checked in to — the dead
                      phone this exists for — appears only here. */}
                  {!visit.note_id &&
                  visit.caregiver_name &&
                  new Date(visit.scheduled_start) <= new Date() ? (
                    <Button variant="secondary" onClick={() => setRecordingFor(visit.id)}>
                      Record note
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </Table>
        ) : null}

        {section === 'feedback' ? (
          <Table columns={['Subject', 'Type', 'Owner', 'State']} empty="No feedback for this client.">
            {data.feedback_cases.map((item) => (
              <tr key={item.id} data-clickable="true" onClick={() => navigate(`/feedback/${item.id}`)}>
                <td className="strong">{item.subject}</td>
                <td>{humanise(item.type)}</td>
                <td>{item.owner_name ?? <span className="meta">Unassigned</span>}</td>
                <td>
                  <Tag
                    label={humanise(item.state)}
                    tone={item.state === 'resolved' ? 'positive' : 'attention'}
                  />
                </td>
              </tr>
            ))}
          </Table>
        ) : null}

        {section === 'access' ? (
          <AccessTab data={data} onChanged={reload} />
        ) : null}
      </div>

      {inviting ? (
        <InviteFamilyModal
          clientId={id!}
          consentRecorded={consentRecorded}
          onClose={() => setInviting(false)}
          onInvited={() => {
            setInviting(false);
            reload();
          }}
        />
      ) : null}

      {recordingFor ? (
        <RecordNoteOnBehalf
          visitId={recordingFor}
          onClose={() => setRecordingFor(null)}
          onDone={() => {
            setRecordingFor(null);
            reload();
          }}
        />
      ) : null}
    </Page>
  );
}

function PlanTab({ data }: { data: ClientRecord }) {
  const plan = data.current_plan;
  if (!plan) {
    return (
      <Notice tone="attention" title="No published care plan">
        Caregivers cannot document a visit until a plan is published, because a note records the
        status of planned tasks.
      </Notice>
    );
  }

  const sections: [string, string][] = [
    ['goals', 'Goals'],
    ['services', 'Services'],
    ['tasks', 'Tasks'],
    ['preferences', 'Preferences'],
    ['risks', 'Risks'],
    ['contacts', 'Contacts'],
    ['schedule', 'Schedule'],
  ];

  return (
    <div className="stack">
      <Card variant="positive">
        <span className="kicker" style={{ color: 'inherit' }}>
          Current care plan
        </span>
        <h2 style={{ margin: 'var(--space-2) 0' }}>Version {plan.version_number}</h2>
        <p className="muted">
          Effective {formatDate(plan.effective_date)}
          {plan.published_at ? ` · published ${formatDate(plan.published_at)}` : ''}
        </p>
      </Card>

      <div className="grid-2">
        {sections.map(([key, label]) => {
          const items = plan.sections[key] ?? [];
          return (
            <Card key={key}>
              <div className="row row--between" style={{ marginBottom: 'var(--space-2)' }}>
                <h3>{label}</h3>
                <span className="meta">{items.length}</span>
              </div>
              {items.length === 0 ? (
                <p className="meta">Nothing recorded.</p>
              ) : (
                <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                  {items.map((item, index) => {
                    const { title, note } = describePlanItem(key, item);
                    return (
                      <li key={item.id ?? index} style={{ marginBottom: 4 }}>
                        {title}
                        {note ? <span className="meta"> — {note}</span> : null}
                        {item.visibility && item.visibility !== 'family_visible' ? (
                          <span className="meta"> · {humanise(item.visibility)}</span>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function AccessTab({ data, onChanged }: { data: ClientRecord; onChanged: () => void }) {
  const { recentAuth } = useSession();
  const [revoking, setRevoking] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  async function revoke(grantId: string) {
    setRevoking(grantId);
    setProblem(null);
    try {
      await api.del(`/admin/grants/${grantId}`);
      onChanged();
    } catch (caught) {
      const error = caught as ApiError;
      setProblem(
        error.needsReauth
          ? 'Revoking access requires a recent sign-in. Sign out and back in, then try again.'
          : error.message,
      );
    } finally {
      setRevoking(null);
    }
  }

  return (
    <div className="stack">
      {problem ? (
        <Notice tone="urgent" title="Could not revoke">
          {problem}
        </Notice>
      ) : null}

      {!recentAuth ? (
        <Notice tone="info" title="Revoking access needs a recent sign-in">
          Access removal takes effect immediately and ends the person&apos;s active session, so it
          is treated as a risky action.
        </Notice>
      ) : null}

      <Table
        columns={['Person', 'Role', 'Relationship', 'Can rate', 'Account', '']}
        empty="Nobody has access to this record yet."
      >
        {data.authorized_people.map((person) => (
          <tr key={person.grant_id}>
            <td>
              <div className="strong">{person.full_name}</div>
              <div className="meta">{person.email ?? 'No email'}</div>
            </td>
            <td>{humanise(person.role)}</td>
            <td>{person.relationship ?? <span className="meta">—</span>}</td>
            <td>{person.can_rate ? 'Yes' : 'No'}</td>
            <td>
              <Tag
                label={humanise(person.account_status)}
                tone={person.account_status === 'active' ? 'positive' : 'attention'}
              />
            </td>
            <td style={{ textAlign: 'right' }}>
              <Button
                variant="urgent"
                onClick={() => revoke(person.grant_id)}
                busy={revoking === person.grant_id}
              >
                Revoke
              </Button>
            </td>
          </tr>
        ))}
      </Table>

      {data.pending_invitations.length > 0 ? (
        <Card variant="flush">
          <CardHead title="Invitations not yet accepted" />
          <Table columns={['Name', 'Email', 'Role', 'Expires']}>
            {data.pending_invitations.map((invitation) => (
              <tr key={invitation.id}>
                <td className="strong">{invitation.full_name}</td>
                <td>{invitation.email}</td>
                <td>{humanise(invitation.role)}</td>
                <td className="meta">{formatDate(invitation.expires_at)}</td>
              </tr>
            ))}
          </Table>
        </Card>
      ) : null}
    </div>
  );
}

function InviteFamilyModal({
  clientId,
  consentRecorded,
  onClose,
  onInvited,
}: {
  clientId: string;
  consentRecorded: boolean;
  onClose: () => void;
  onInvited: () => void;
}) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [relationship, setRelationship] = useState('');
  const [consentReference, setConsentReference] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setProblem(null);
    try {
      const response = await api.post<{ invitation_token: string }>('/admin/invitations', {
        email: email.trim(),
        full_name: fullName.trim(),
        role: 'family',
        care_recipient_id: clientId,
        relationship: relationship.trim() || null,
        consent_reference: consentReference.trim(),
      });
      setToken(response.invitation_token);
    } catch (caught) {
      setProblem((caught as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  if (token) {
    return (
      <Modal title="Invitation created" onClose={onInvited}>
        <div className="stack">
          <Notice tone="positive" title="Send this link out of band">
            It is shown once and never again.
          </Notice>
          <Card>
            <code className="numeric" style={{ wordBreak: 'break-all' }}>
              familycare://invite?token={token}
            </code>
          </Card>
          <Button onClick={onInvited} block>
            Done
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Invite a family member" onClose={onClose}>
      <div className="stack">
        {!consentRecorded ? (
          <Notice tone="urgent" title="Consent is not recorded for this client">
            The agency must record legal authority or consent before a family member can be given
            access. This invitation will be refused.
          </Notice>
        ) : null}

        {problem ? (
          <Notice tone="urgent" title="Could not create the invitation">
            {problem}
          </Notice>
        ) : null}

        <Field label="Full name">
          {(props) => (
            <TextInput {...props} value={fullName} onChange={(e) => setFullName(e.target.value)} autoFocus />
          )}
        </Field>
        <Field label="Email">
          {(props) => (
            <TextInput {...props} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          )}
        </Field>
        <Field label="Relationship" hint="Daughter, son, spouse…">
          {(props) => (
            <TextInput
              {...props}
              value={relationship}
              onChange={(event) => setRelationship(event.target.value)}
            />
          )}
        </Field>
        <Field
          label="Consent or legal authority reference"
          hint="Required. Access is not granted until authorization is recorded."
        >
          {(props) => (
            <TextInput
              {...props}
              value={consentReference}
              onChange={(event) => setConsentReference(event.target.value)}
            />
          )}
        </Field>

        <div className="row row--between">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            busy={busy}
            disabled={!fullName.trim() || !email.trim() || !consentReference.trim()}
          >
            Create invitation
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Whether this client's care is actually covered.
 *
 * The schedule screen already says all of this, but a coordinator lands here,
 * and a gap nobody navigates to is a gap nobody fixes. So the warning follows
 * the person rather than living where the data does.
 *
 * Silent when there is nothing wrong. A banner that is always present stops
 * being read, and this one has to still work on the day it matters.
 */
function ScheduleGap({ health, clientId }: { health: ScheduleHealth; clientId: string }) {
  const navigate = useNavigate();
  if (!health) return null;

  // No published plan is already said loudly elsewhere on this page, and
  // repeating it here would push the real warnings down.
  if (!health.has_plan) return null;

  const problems: string[] = [];
  if (health.uncovered_blocks > 0) {
    problems.push(
      `${health.uncovered_blocks} visit${health.uncovered_blocks === 1 ? '' : 's'} a week ` +
        `${health.uncovered_blocks === 1 ? 'has' : 'have'} nobody assigned`,
    );
  }
  if (health.visits_outside_plan > 0) {
    problems.push(
      `${health.visits_outside_plan} booked visit${health.visits_outside_plan === 1 ? '' : 's'} ` +
        'the care plan no longer covers',
    );
  }
  if (problems.length === 0) return null;

  return (
    <Notice tone="urgent" title="This client's schedule has a gap">
      <ul>
        {problems.map((problem) => (
          <li key={problem}>{problem}</li>
        ))}
      </ul>
      <Button variant="secondary" onClick={() => navigate(`/clients/${clientId}/schedule`)}>
        Open the schedule
      </Button>
    </Notice>
  );
}
